import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { gifts } from "../lib/api";
import {
  Sparkles,
  CheckCircle2,
  Mail,
  CalendarClock,
  Gift,
  ArrowRight,
  Loader2,
} from "lucide-react";

// Format a UTC ISO string for display in the recipient's timezone (if we
// captured one). Falls back to the visitor's local zone.
const formatSendAt = (iso, tz) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const opts = {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: tz || undefined,
      timeZoneName: "short",
    };
    return d.toLocaleString(undefined, opts);
  } catch (_) {
    return iso;
  }
};

export default function GiftSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [gift, setGift] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Force top-of-page and clear any lingering scroll state from the checkout.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Poll the session endpoint briefly — Stripe's webhook is fast but the
  // shopper can arrive at /gift/success within milliseconds of the payment,
  // sometimes before the webhook has flipped status to paid/scheduled. Try
  // up to 6x over ~6s.
  useEffect(() => {
    if (!sessionId) {
      setError("Missing session id.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const data = await gifts.session(sessionId);
        if (cancelled) return;
        setGift(data);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        if (attempts < 6) {
          setTimeout(tick, 1000);
        } else {
          setError(err?.response?.data?.detail || "Could not find your gift.");
          setLoading(false);
        }
      }
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const recipient = gift?.recipient_name || "your recipient";
  const isScheduled = gift?.status === "scheduled" && gift?.send_at;
  const sendAtDisplay = isScheduled
    ? formatSendAt(gift.send_at, gift.recipient_timezone)
    : null;

  return (
    <div className="min-h-screen bg-[#F9FAF7] text-[#2E2E2E]">
      {/* Top nav */}
      <header className="border-b border-[#DCE5DB]/70 bg-white/70 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2.5"
            data-testid="gift-success-home-link"
          >
            <div className="w-9 h-9 rounded-2xl bg-[#4F6F52] flex items-center justify-center shadow-[0_4px_14px_rgba(79,111,82,0.25)]">
              <Sparkles size={16} className="text-[#F9FAF7]" strokeWidth={1.8} />
            </div>
            <span className="font-display text-xl text-[#4F6F52]">LifeGuide</span>
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-14 sm:py-20">
        {loading && (
          <div
            className="text-center text-[#6B6B6B] py-24 flex flex-col items-center gap-4"
            data-testid="gift-success-loading"
          >
            <Loader2 size={28} className="text-[#4F6F52] animate-spin" strokeWidth={1.6} />
            <p className="text-sm">Confirming your gift…</p>
          </div>
        )}

        {!loading && error && (
          <div
            className="rounded-3xl bg-white border border-[#DCE5DB] p-8 text-center shadow-[0_4px_20px_rgba(0,0,0,0.03)]"
            data-testid="gift-success-error"
          >
            <p className="font-display text-2xl text-[#2E2E2E] mb-2">
              We couldn't find that gift.
            </p>
            <p className="text-sm text-[#6B6B6B] mb-6">{error}</p>
            <Link
              to="/gift"
              className="inline-flex items-center gap-2 bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-6 py-3 font-medium transition"
              data-testid="gift-success-retry"
            >
              Back to gifts
              <ArrowRight size={14} />
            </Link>
          </div>
        )}

        {!loading && !error && gift && (
          <div className="fade-in-up" data-testid="gift-success-page">
            {/* Hero */}
            <div className="text-center mb-10">
              <div className="w-16 h-16 rounded-full bg-[#E1ECDD] border border-[#4F6F52]/25 flex items-center justify-center mx-auto mb-6 shadow-[0_4px_18px_rgba(79,111,82,0.18)]">
                <CheckCircle2 size={30} className="text-[#4F6F52]" strokeWidth={1.8} />
              </div>
              <h1 className="font-display text-3xl sm:text-4xl text-[#2E2E2E] mb-3">
                Your Gift Is On Its Way ✓
              </h1>
              <p className="text-[#6B6B6B] text-lg">
                Thank you for giving LifeGuide.
              </p>
            </div>

            {/* Receipt card */}
            <div className="rounded-3xl bg-white border border-[#DCE5DB] p-6 sm:p-8 shadow-[0_6px_28px_rgba(0,0,0,0.04)] space-y-5">
              <div className="rounded-2xl bg-[#F5EBD9]/50 border border-[#D6C7A1]/50 px-4 py-3.5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Gift size={16} className="text-[#8C7A4F]" />
                  <div>
                    <div
                      className="text-sm font-semibold text-[#2E2E2E]"
                      data-testid="gift-success-duration"
                    >
                      {gift.duration_months} month{gift.duration_months === 1 ? "" : "s"} of LifeGuide
                    </div>
                    <div className="text-xs text-[#6B6B6B]">
                      For{" "}
                      <span data-testid="gift-success-recipient">{recipient}</span>
                    </div>
                  </div>
                </div>
                {typeof gift.amount_cents === "number" && (
                  <div className="font-display text-lg text-[#8C7A4F]">
                    ${(gift.amount_cents / 100).toFixed(2)}
                  </div>
                )}
              </div>

              {/* Delivery status */}
              {isScheduled ? (
                <div
                  className="flex items-start gap-3 text-sm text-[#2E2E2E] rounded-2xl bg-[#EEF3EC] border border-[#DCE5DB] px-4 py-4"
                  data-testid="gift-success-scheduled"
                >
                  <CalendarClock size={16} className="text-[#4F6F52] mt-0.5 shrink-0" />
                  <div className="leading-relaxed">
                    Your LifeGuide gift for <strong>{recipient}</strong> is scheduled for{" "}
                    <strong>{sendAtDisplay}</strong>.
                  </div>
                </div>
              ) : (
                <div
                  className="flex items-start gap-3 text-sm text-[#2E2E2E] rounded-2xl bg-[#EEF3EC] border border-[#DCE5DB] px-4 py-4"
                  data-testid="gift-success-sending-now"
                >
                  <Mail size={16} className="text-[#4F6F52] mt-0.5 shrink-0" />
                  <div className="leading-relaxed">
                    We'll send your LifeGuide gift to{" "}
                    <strong>{recipient}</strong> by email shortly.
                  </div>
                </div>
              )}

              <p className="text-xs text-[#6B6B6B] leading-relaxed text-center pt-1">
                This was a one-time purchase. It will not auto-renew — your recipient
                receives {gift.duration_months} month
                {gift.duration_months === 1 ? "" : "s"} of Pro access and no further
                charges.
              </p>
            </div>

            {/* CTA */}
            <div className="mt-10 text-center">
              <Link
                to="/"
                data-testid="gift-success-return-btn"
                className="inline-flex items-center gap-2 bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-8 py-4 font-medium transition shadow-[0_6px_20px_rgba(79,111,82,0.22)]"
              >
                Return to LifeGuide
                <ArrowRight size={15} strokeWidth={2} />
              </Link>
              <div className="mt-4 text-xs text-[#6B6B6B]">
                <Link
                  to="/gift"
                  className="underline underline-offset-4 hover:text-[#4F6F52]"
                  data-testid="gift-success-give-another"
                >
                  Give another gift
                </Link>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
